import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/features/auth';
import { useProject } from '@/hooks/useProjects';
import { useProductMeta } from '@/hooks/meta/useProductMeta';
import { AlertCircle, CheckCircle, Globe, Loader2, Send, XCircle } from 'lucide-react';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

interface EdgeFunctionResponse {
  success: boolean;
  data?: any;
  error?: string;
  status?: number;
  responseTime?: number;
}

export function EdgeFunctionCaller() {
  const [url, setUrl] = useState('');
  const [method, setMethod] = useState('POST');
  const [requestBody, setRequestBody] = useState('{}');
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<EdgeFunctionResponse | null>(null);
  const { session } = useAuth();
  const { tenant } = useProductMeta();

  // Get context IDs
  const { projectId } = useParams();
  const { data: project } = useProject(projectId, { includeCurrentStory: true });
  const storyId = project?.current_story_id?.toString();

  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch {
      return false;
    }
  };

  const callEdgeFunction = async () => {
    if (!url.trim()) {
      setResponse({
        success: false,
        error: 'URL is required'
      });
      return;
    }

    // Only validate JSON for methods that typically have a body
    const methodsWithBody = ['POST', 'PUT', 'PATCH'];
    if (methodsWithBody.includes(method) && !validateJson(requestBody)) {
      setResponse({
        success: false,
        error: 'Request body must be valid JSON'
      });
      return;
    }

    if (!session?.access_token) {
      setResponse({
        success: false,
        error: 'No authentication session found'
      });
      return;
    }

    setIsLoading(true);
    setResponse(null);

    try {
      const startTime = performance.now();

      // Get tenant ID
      const tenantId = tenant?.id;

      const headers: Record<string, string> = {
        'Authorization': `Bearer ${session.access_token}`
      };

      // Add custom headers for edge functions
      if (tenantId) {
        headers['x-tenant-id'] = tenantId;
      }
      if (projectId) {
        headers['x-project-id'] = projectId;
      }
      if (storyId) {
        headers['x-story-id'] = storyId;
      }

      // Only add Content-Type and body for methods that typically have a body
      const methodsWithBody = ['POST', 'PUT', 'PATCH'];
      const requestOptions: RequestInit = {
        method,
        headers
      };

      if (methodsWithBody.includes(method)) {
        headers['Content-Type'] = 'application/json';
        const body = JSON.parse(requestBody);
        requestOptions.body = JSON.stringify(body);
      }

      const fetchResponse = await fetch(url, requestOptions);

      const endTime = performance.now();
      const responseTime = endTime - startTime;

      let responseData;
      const contentType = fetchResponse.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseData = await fetchResponse.json();
      } else {
        responseData = await fetchResponse.text();
      }

      setResponse({
        success: fetchResponse.ok,
        data: responseData,
        status: fetchResponse.status,
        responseTime,
        error: fetchResponse.ok ? undefined : `HTTP ${fetchResponse.status}: ${fetchResponse.statusText}`
      });

    } catch (error) {
      console.error('Error calling edge function:', error);
      setResponse({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    callEdgeFunction();
  };

  const getStatusIcon = () => {
    if (isLoading) {
      return <Loader2 className="h-5 w-5 animate-spin text-blue-600" />;
    }
    if (response?.success) {
      return <CheckCircle className="h-5 w-5 text-green-600" />;
    }
    if (response && !response.success) {
      return <XCircle className="h-5 w-5 text-red-600" />;
    }
    return <Globe className="h-5 w-5 text-gray-600" />;
  };

  const getStatusBadge = () => {
    if (isLoading) {
      return <Badge variant="secondary">Calling...</Badge>;
    }
    if (response?.success) {
      return <Badge variant="default" className="bg-green-100 text-green-800">Success</Badge>;
    }
    if (response && !response.success) {
      return <Badge variant="destructive">Error</Badge>;
    }
    return <Badge variant="outline">Ready</Badge>;
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getStatusIcon()}
            <CardTitle>Edge Function Caller</CardTitle>
          </div>
          {getStatusBadge()}
        </div>
        <CardDescription>
          Call any edge function with authenticated requests using your Supabase session token
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-3">
              <Label htmlFor="url">Function URL</Label>
              <Input
                id="url"
                type="url"
                placeholder="https://your-project.supabase.co/functions/v1/your-function"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="method">HTTP Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GET">GET</SelectItem>
                  <SelectItem value="POST">POST</SelectItem>
                  <SelectItem value="PUT">PUT</SelectItem>
                  <SelectItem value="PATCH">PATCH</SelectItem>
                  <SelectItem value="DELETE">DELETE</SelectItem>
                  <SelectItem value="HEAD">HEAD</SelectItem>
                  <SelectItem value="OPTIONS">OPTIONS</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Only show request body for methods that typically have a body */}
          {['POST', 'PUT', 'PATCH'].includes(method) && (
            <div>
              <Label htmlFor="requestBody">Request Body (JSON)</Label>
              <Textarea
                id="requestBody"
                placeholder='{"key": "value", "data": "your data here"}'
                value={requestBody}
                onChange={(e) => setRequestBody(e.target.value)}
                className="mt-1 font-mono text-sm"
                rows={6}
              />
              {requestBody && !validateJson(requestBody) && (
                <p className="text-sm text-red-600 mt-1">Invalid JSON format</p>
              )}
            </div>
          )}

          <Button
            type="submit"
            disabled={isLoading || !url.trim() || (['POST', 'PUT', 'PATCH'].includes(method) && !validateJson(requestBody))}
            className="w-full"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Calling Function...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                {method} Request
              </>
            )}
          </Button>
        </form>

        {/* Authentication Status */}
        <div className="space-y-2">
          <div className="text-sm font-medium">Authentication Status</div>
          <div className="flex items-center gap-2">
            {session?.access_token ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <XCircle className="h-4 w-4 text-red-600" />
            )}
            <span className="text-sm">
              {session?.access_token ? 'Authenticated' : 'Not authenticated'}
            </span>
          </div>
        </div>

        {/* Context Information */}
        <div className="space-y-2">
          <div className="text-sm font-medium">Context Headers</div>
          <div className="bg-muted/30 rounded-lg p-3 space-y-1 text-xs font-mono">
            <div className="flex justify-between">
              <span className="text-muted-foreground">x-project-id:</span>
              <span>{projectId || 'Not available'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">x-story-id:</span>
              <span>{storyId || 'Not available'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">x-tenant-id:</span>
              <span>Will be fetched on request</span>
            </div>
          </div>
        </div>

        {/* Response Display */}
        {response && (
          <div className="space-y-4">
            <div className="text-sm font-medium">Response</div>

            {/* Response Status */}
            <div className="flex items-center gap-4 text-sm">
              {response.status && (
                <span>Status: <code className="bg-muted px-1 rounded">{response.status}</code></span>
              )}
              {response.responseTime && (
                <span>Time: <code className="bg-muted px-1 rounded">{Math.round(response.responseTime)}ms</code></span>
              )}
            </div>

            {/* Error Display */}
            {response.error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Error:</strong> {response.error}
                </AlertDescription>
              </Alert>
            )}

            {/* Success Response */}
            {response.success && response.data && (
              <div className="space-y-2">
                <div className="text-sm font-medium text-green-700">Response Data</div>
                <pre className="bg-muted/30 rounded-lg p-3 text-xs overflow-auto max-h-96">
                  {typeof response.data === 'string'
                    ? response.data
                    : JSON.stringify(response.data, null, 2)
                  }
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Usage Instructions */}
        <div className="space-y-3">
          <div className="text-sm font-medium">Usage Instructions</div>
          <div className="bg-muted/30 rounded-lg p-3 space-y-2 text-sm">
            <div><strong>1.</strong> Enter the full URL of your edge function</div>
            <div><strong>2.</strong> Select the HTTP method (GET, POST, PUT, PATCH, DELETE, etc.)</div>
            <div><strong>3.</strong> For POST/PUT/PATCH requests, provide the request body as valid JSON</div>
            <div><strong>4.</strong> The function will be called with your current Supabase session token</div>
            <div><strong>5.</strong> Custom headers (x-tenant-id, x-project-id, x-story-id) are automatically included</div>
            <div><strong>6.</strong> View the response data and status below</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
